import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { DEFAULT_DICT } from "@/contexts/I18nContext";
import { TEXT_SECTIONS } from "@/lib/textSchema";
import { getPath, setPath, pruneEmpty } from "@/lib/textOverrides";

/**
 * Editable text overrides — bilingual (FI + EN side-by-side), grouped by section.
 * Empty field = use the default text shipped with the site.
 */
export default function AdminTextsSection() {
  const [overrides, setOverrides] = useState({ fi: {}, en: {} });
  const [activeId, setActiveId] = useState(TEXT_SECTIONS[0].id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .getTexts()
      .then((data) =>
        setOverrides({ fi: data?.fi || {}, en: data?.en || {} }),
      )
      .finally(() => setLoading(false));
  }, []);

  const currentSection =
    TEXT_SECTIONS.find((s) => s.id === activeId) || TEXT_SECTIONS[0];

  const getOverride = (lang, path) => getPath(overrides[lang], path) ?? "";
  const getDefault = (lang, path) => getPath(DEFAULT_DICT[lang], path) ?? "";

  const setOverride = (lang, path, value) => {
    setOverrides((prev) => ({
      ...prev,
      [lang]: setPath(prev[lang], path, value),
    }));
  };

  const resetField = (path) => {
    setOverrides((prev) => ({
      fi: setPath(prev.fi, path, ""),
      en: setPath(prev.en, path, ""),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      // Prune empty strings → backend stores only real overrides
      const payload = {
        fi: pruneEmpty(overrides.fi) || {},
        en: pruneEmpty(overrides.en) || {},
      };
      await api.putTexts(payload);
      window.dispatchEvent(new CustomEvent("anypaku-texts-changed"));
      setSavedAt(Date.now());
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    if (
      !window.confirm(
        "Réinitialiser TOUS les textes aux valeurs par défaut ? Cette action supprime toutes vos modifications.",
      )
    )
      return;
    setOverrides({ fi: {}, en: {} });
    setSaving(true);
    setError("");
    try {
      await api.putTexts({ fi: {}, en: {} });
      window.dispatchEvent(new CustomEvent("anypaku-texts-changed"));
      setSavedAt(Date.now());
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section
        className="rounded-3xl p-6 md:p-8"
        style={{ background: "#FFFFFF", border: "1px solid var(--line)" }}
      >
        <div className="text-sm" style={{ color: "var(--mute)" }}>
          Chargement des textes…
        </div>
      </section>
    );
  }

  const justSaved = savedAt && Date.now() - savedAt < 2500;

  return (
    <section
      className="rounded-3xl p-6 md:p-8"
      style={{ background: "#FFFFFF", border: "1px solid var(--line)" }}
      data-testid="admin-texts-section"
    >
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="display" style={{ fontSize: 26, color: "var(--navy)" }}>
          📝 Textes du site
        </h2>
        <button
          type="button"
          onClick={handleResetAll}
          className="text-xs"
          style={{ color: "#B3263F" }}
          data-testid="admin-texts-reset-all"
        >
          Tout réinitialiser
        </button>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--mute)" }}>
        Édite les textes du site dans les deux langues. Laisse un champ <strong>vide</strong> pour
        utiliser le texte par défaut.
      </p>

      {/* Section tabs */}
      <div
        className="flex flex-wrap gap-2 mb-5 pb-4"
        style={{ borderBottom: "1px solid var(--line)" }}
        data-testid="admin-texts-tabs"
      >
        {TEXT_SECTIONS.map((s) => {
          const active = s.id === activeId;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setActiveId(s.id)}
              className="px-3 py-2 rounded-xl text-sm font-bold transition-colors"
              style={{
                background: active ? "var(--navy)" : "var(--cream-2)",
                color: active ? "#FFFFFF" : "var(--navy)",
                border: active ? "1px solid var(--navy)" : "1px solid transparent",
              }}
              data-testid={`admin-texts-tab-${s.id}`}
            >
              <span className="mr-1.5">{s.icon}</span>
              {s.title}
            </button>
          );
        })}
      </div>

      {/* Field grid */}
      <div className="space-y-5" data-testid={`admin-texts-panel-${currentSection.id}`}>
        {currentSection.fields.map((field) => {
          const fiDefault = getDefault("fi", field.path);
          const enDefault = getDefault("en", field.path);
          const fiVal = getOverride("fi", field.path);
          const enVal = getOverride("en", field.path);
          const hasOverride =
            (typeof fiVal === "string" && fiVal.length > 0) ||
            (typeof enVal === "string" && enVal.length > 0);

          const InputCmp = field.type === "textarea" ? "textarea" : "input";
          const commonProps = {
            className:
              "w-full px-3 py-2 rounded-xl border outline-none text-[14px] bg-white",
            style: { borderColor: "#DCE3ED", color: "var(--navy)" },
          };

          return (
            <div
              key={field.path}
              className="grid grid-cols-1 md:grid-cols-2 gap-3"
              data-testid={`admin-texts-field-${field.path}`}
            >
              <div className="md:col-span-2 flex items-center justify-between">
                <div
                  className="text-xs font-bold"
                  style={{
                    color: "var(--navy)",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  {field.label}
                </div>
                {hasOverride && (
                  <button
                    type="button"
                    onClick={() => resetField(field.path)}
                    className="text-xs"
                    style={{ color: "var(--orange-2)" }}
                    data-testid={`admin-texts-reset-${field.path}`}
                  >
                    ↺ Restaurer défaut
                  </button>
                )}
              </div>

              {/* FI */}
              <div>
                <div
                  className="text-[10px] mb-1"
                  style={{
                    color: "var(--mute)",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  🇫🇮 Finnois
                </div>
                <InputCmp
                  {...commonProps}
                  rows={field.type === "textarea" ? 3 : undefined}
                  value={fiVal}
                  placeholder={fiDefault}
                  onChange={(e) =>
                    setOverride("fi", field.path, e.target.value)
                  }
                  data-testid={`admin-texts-fi-${field.path}`}
                />
              </div>

              {/* EN */}
              <div>
                <div
                  className="text-[10px] mb-1"
                  style={{
                    color: "var(--mute)",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  🇬🇧 Anglais
                </div>
                <InputCmp
                  {...commonProps}
                  rows={field.type === "textarea" ? 3 : undefined}
                  value={enVal}
                  placeholder={enDefault}
                  onChange={(e) =>
                    setOverride("en", field.path, e.target.value)
                  }
                  data-testid={`admin-texts-en-${field.path}`}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Save bar (local to texts) */}
      <div
        className="flex flex-col sm:flex-row items-center gap-3 justify-between mt-6 pt-5"
        style={{ borderTop: "1px solid var(--line)" }}
      >
        <div className="text-sm" style={{ color: "var(--mute)" }}>
          {error ? (
            <span style={{ color: "#B3263F" }}>⚠️ {error}</span>
          ) : justSaved ? (
            <span style={{ color: "#1A6F3A" }}>
              ✓ Textes enregistrés — visibles immédiatement sur le site
            </span>
          ) : (
            "Les champs vides utilisent le texte par défaut."
          )}
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
          style={{ padding: "10px 20px", fontSize: 14 }}
          data-testid="admin-texts-save"
        >
          {saving ? "Enregistrement…" : "💾 Enregistrer les textes"}
        </button>
      </div>
    </section>
  );
}
